// Search metadata for each route. public/index.html carries the site-wide
// defaults plus the Open Graph tags and JSON-LD that link-preview scrapers
// read (they don't run JavaScript). On every navigation, applyPageMeta()
// swaps in the route's title, description, canonical URL and robots
// directive, which is what Google indexes once it has rendered the page.
//
// Routes are matched on the full path first, then on shorter prefixes
// (/showcase/abc/similar -> /showcase/abc -> /showcase). A path with no entry
// gets the default title and is NOT indexed, so a new private page can't leak
// into search by being forgotten here. Every entry with index: true must also
// be listed in public/sitemap.xml.

export const SITE_URL = "https://deepthought.fortytwodegrees.org";
const SITE_HOST = new URL(SITE_URL).hostname;

const DEFAULT_TITLE = "DeepThought: AI Manuscript Novelty, Methods & Impact Scores";
const DEFAULT_DESCRIPTION =
    "Score your research manuscript's Novelty, Methodology and Impact with graph network analysis of " +
    "250,000+ microbiology and virology papers. Free, fast and private.";

const INDEX = "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1";

const PAGES = {
    // ── Public, indexed ─────────────────────────────────────
    "/": {
        index: true,
        title: DEFAULT_TITLE,
        description: DEFAULT_DESCRIPTION,
    },
    "/tutorial": {
        index: true,
        title: "DeepThought Tutorial: See a Real Manuscript Analysis",
        description:
            "A two-minute guided tour of a real paper analyzed by DeepThought: Novelty, Methodology and " +
            "Impact scores, written analysis, field comparison, the research network, DeepThinkers and similar work.",
    },
    "/funders": {
        index: true,
        title: "42Degrees for Funders: Research Intelligence for Grantmakers",
        description:
            "Score every grant proposal on novelty, methodology and impact with an interpretable knowledge " +
            "graph. Confidence bands and source provenance help funders triage, discover and decide.",
    },
    "/faq": {
        index: true,
        title: "DeepThought FAQ: How Manuscript Scoring Works",
        description:
            "How DeepThought scores Novelty, Methodology and Impact, how accurate the scores are, which papers " +
            "it supports, how DeepThinkers are found, and how your manuscript stays private.",
    },
    "/about": {
        index: true,
        title: "About DeepThought and 42Degrees",
        description:
            "DeepThought is 42Degrees' research analytics platform. It scores manuscripts by where they sit in " +
            "a knowledge graph of published science, not by citations or popularity.",
    },
    "/sample/submission": {
        index: true,
        title: "Sample DeepThought Report: Novelty, Methods & Impact",
        description:
            "An example DeepThought analysis of a published paper: Novelty, Methodology and Impact scores, " +
            "the most similar research, and the paper's place in the research network.",
    },
    "/register": {
        index: true,
        title: "Create a Free Account | DeepThought",
        description:
            "Sign up free to score your manuscripts on Novelty, Methodology and Impact. No credit card " +
            "required, and your manuscripts are never used for training.",
    },
    "/login": {
        index: true,
        title: "Log In | DeepThought",
        description: "Log in to DeepThought to upload manuscripts and view your Novelty, Methodology and Impact scores.",
    },
    "/privacy": {
        index: true,
        title: "Privacy Policy | DeepThought by 42Degrees",
        description:
            "How 42Degrees collects, uses and protects your information and manuscripts when you use DeepThought.",
    },
    "/terms": {
        index: true,
        title: "Terms of Service | DeepThought by 42Degrees",
        description: "The terms that govern your use of DeepThought and the 42Degrees platform.",
    },

    // ── Public, but kept out of search ──────────────────────
    // Thin, transactional or per-person pages, share links, and demo portals.
    "/sample/similar-works": { title: "Sample Similar Research | DeepThought" },
    "/sample/network": { title: "Sample Research Network | DeepThought" },
    "/showcase": { title: "Showcase Paper | DeepThought" },
    "/funder": { title: "Funder Portal Demo | DeepThought" },
    "/cfri": { title: "CFRI proposal review | DeepThought" },
    "/forgot-password": { title: "Forgot Password | DeepThought" },
    "/reset-password": { title: "Reset Password | DeepThought" },
    "/report/verify": { title: "Verify a Report | DeepThought" },
    "/feedback": { title: "Send Feedback | DeepThought" },
    "/onboard": { title: "Welcome | DeepThought" },
    "/oauth/authorize": { title: "Connect an App | DeepThought" },
    "/error": { title: "Something Went Wrong | DeepThought" },

    // ── Signed-in app (also disallowed in robots.txt) ───────
    "/upload": { title: "Upload a Manuscript | DeepThought" },
    "/submissions": { title: "Submissions | DeepThought" },
    "/submission": { title: "Submission Report | DeepThought" },
    "/similar-works": { title: "Similar Research | DeepThought" },
    "/network": { title: "Research Network | DeepThought" },
    "/deepthinkers": { title: "DeepThinkers | DeepThought" },
    "/peer-reviews": { title: "Peer Reviews | DeepThought" },
    "/reviewer": { title: "Reviewer Dashboard | DeepThought" },
    "/analytics": { title: "Analytics | DeepThought" },
    "/profile": { title: "Researcher Profile | DeepThought" },
    "/settings": { title: "Settings | DeepThought" },
    "/change-password": { title: "Change Password | DeepThought" },
    "/admin": { title: "Admin | DeepThought" },
};

function findPage(pathname) {
    const path = pathname.replace(/\/+$/, "") || "/";
    if (PAGES[path]) return PAGES[path];
    // Walk up the path, stopping short of "/" (the home page, not a catch-all)
    const parts = path.split("/");
    for (let n = parts.length - 1; n > 1; n--) {
        const prefix = parts.slice(0, n).join("/");
        if (PAGES[prefix]) return PAGES[prefix];
    }
    return {};
}

function setAttr(selector, attr, value) {
    document.head.querySelector(selector)?.setAttribute(attr, value);
}

export function applyPageMeta(pathname) {
    const page = findPage(pathname);
    const title = page.title || DEFAULT_TITLE;
    const description = page.description || DEFAULT_DESCRIPTION;
    // Canonical is always the production host with no query string, so the
    // second prod domain (score.) and ad-tagged URLs (?utm_…) fold into one.
    const url = SITE_URL + (pathname.replace(/\/+$/, "") || "/");
    // Only the canonical production host is ever indexed; dev, score. and
    // localhost all serve this same bundle.
    const robots = window.location.hostname !== SITE_HOST ? "noindex, nofollow"
        : page.index ? INDEX
        : "noindex, follow";

    document.title = title;
    setAttr('meta[name="description"]', "content", description);
    setAttr('meta[name="robots"]', "content", robots);
    setAttr('link[rel="canonical"]', "href", url);
    setAttr('meta[property="og:url"]', "content", url);
    setAttr('meta[property="og:title"]', "content", title);
    setAttr('meta[property="og:description"]', "content", description);
    setAttr('meta[name="twitter:title"]', "content", title);
    setAttr('meta[name="twitter:description"]', "content", description);
}
